/**
 * YouTube URL helpers.
 *
 * One place that answers "what is the video id here" and "what URL should we embed",
 * because the values reaching the front end are not consistent: the `youtube_video_id`
 * ACF field holds bare ids, ids with a stray suffix pasted from a timestamped link
 * ("abc123&t"), and occasionally a full youtube.com URL.
 *
 * Two rules are enforced here rather than at each call site:
 *
 *   - the embed host is always youtube-nocookie.com, never youtube.com;
 *   - `enablejsapi` is never added. It loads youtube.com alongside the nocookie host and
 *     brings storage the consent tooling has to account for. Where playback needs to be
 *     stopped, the iframe is removed instead.
 *
 * Loaded from src/js/utils, which gulp concatenates ahead of src/js, so it is available
 * to every other script. Exposed on window because each file compiles to its own IIFE.
 */

interface EsrbYouTubeApi {
	/** Video id from a bare id, watch URL, youtu.be link or embed URL. '' if none. */
	id( value: string ): string;
	/** Nocookie embed URL for the same inputs, with optional query. '' if not YouTube. */
	embed( value: string, params?: string ): string;
}

(function () {
	'use strict';

	var URL_PATTERNS = [
		/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{6,})/i,
		/youtu\.be\/([A-Za-z0-9_-]{6,})/i,
		/youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})/i,
		/youtube(?:-nocookie)?\.com\/v\/([A-Za-z0-9_-]{6,})/i
	];

	function id( value: string ): string {
		if ( !value || typeof value !== 'string' ) {
			return '';
		}

		var raw = value.trim();

		if ( '' === raw ) {
			return '';
		}

		// No path or host: treat it as a bare id, keeping only the leading run of valid
		// characters so "abc123&t=30" yields "abc123".
		if ( raw.indexOf( '/' ) === -1 && raw.indexOf( '.' ) === -1 ) {
			var bare = raw.match( /^([A-Za-z0-9_-]{6,})/ );

			return bare ? bare[1] : '';
		}

		for ( var i = 0; i < URL_PATTERNS.length; i++ ) {
			var found = raw.match( URL_PATTERNS[ i ] );

			if ( found ) {
				return found[1];
			}
		}

		return '';
	}

	function embed( value: string, params?: string ): string {
		var videoId = id( value );

		if ( '' === videoId ) {
			return '';
		}

		return 'https://www.youtube-nocookie.com/embed/' + videoId + ( params ? '?' + params : '' );
	}

	var api: EsrbYouTubeApi = { id: id, embed: embed };

	( window as unknown as { EsrbYouTube?: EsrbYouTubeApi } ).EsrbYouTube = api;
})();
